/**
 * Public pages — port từ src/routes/index.tsx + src/routes/bo-suu-tap.$mode.tsx
 * sang Safe Self-Host. Giữ nguyên layout, copy, label tiếng Việt, sticker, edit-mode.
 *
 *  - PublicHomePage      → "/"          (hero + 4 card bộ sưu tập + sticker)
 *  - PublicCollectionPage → "/bo-suu-tap/{mode}"  (mode = all | rental | craft | sample | order)
 *
 * Không yêu cầu đăng nhập. Dùng PHP API qua src/lib/api.ts.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Sparkles, MessageCircle, Hammer, Shirt, Palette, Scissors, Wand2,
  ArrowRight, Clock, ChevronDown, Camera, Package,
  CornerUpLeft, CornerUpRight, ChevronsUp, ArrowLeft, Search, X, Settings,
  Shuffle, Star, ImagePlus, EyeOff,
} from "lucide-react";
import { api } from "@/lib/api";
import { EditableText, useSiteText, useEditMode, EditModeToggle } from "@/static/EditMode";
import { upsertSiteText } from "@/lib/site-texts-service";
import { StickerCanvas } from "@/static/Sticker";
import { ThemeSwitcher } from "@/shared/theme/ThemeSwitcher";

const MESSENGER_URL = "https://fb.com/NanaIndustry";

/* ============================================================ Types */

type ProductMode = "rental" | "craft" | "sample" | "order" | "both";
type Product = {
  id: string; slug: string; name: string;
  description: string | null;
  category: string | null;
  fandom: string | null;
  character_name: string | null;
  materials: string | null;
  price: string | null;
  mode: ProductMode;
  images: string[];
  cover_image: string | null;
  published: boolean;
  is_public: boolean;
  attributes: Record<string, string>;
  image_meta: Record<string, { caption?: string; prompt_log?: string }>;
  tile_w?: number;
  tile_h?: number;
  sort_order?: number;
};

const MODE_LABEL: Record<string, string> = {
  rental: "Cho thuê", craft: "Đặt làm", sample: "Sample", order: "Order", both: "Đặt & Thuê",
};

function normalize(s: string): string {
  return s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/đ/g, "d");
}

function normProduct(row: any): Product {
  const list = (v: unknown): string[] => {
    if (Array.isArray(v)) return v.filter(Boolean).map(String);
    if (typeof v === "string" && v.trim()) {
      try { const p = JSON.parse(v); if (Array.isArray(p)) return p.filter(Boolean).map(String); } catch {}
    }
    return [];
  };
  const rec = (v: unknown): Record<string, string> => {
    if (v && typeof v === "object" && !Array.isArray(v)) return v as Record<string, string>;
    if (typeof v === "string" && v.trim()) {
      try { const p = JSON.parse(v); if (p && typeof p === "object" && !Array.isArray(p)) return p; } catch {}
    }
    return {};
  };
  return {
    id: String(row?.id ?? ""), slug: String(row?.slug ?? row?.id ?? ""),
    name: String(row?.name ?? ""), description: row?.description ?? null,
    category: row?.category ?? null, fandom: row?.fandom ?? null,
    character_name: row?.character_name ?? null, materials: row?.materials ?? null,
    price: row?.price ?? null, mode: (row?.mode ?? "craft") as ProductMode,
    images: list(row?.images), cover_image: row?.cover_image ?? null,
    published: row?.published !== false && row?.published !== 0 && row?.published !== "0",
    is_public: row?.is_public !== false && row?.is_public !== 0 && row?.is_public !== "0",
    attributes: rec(row?.attributes),
    image_meta: rec(row?.image_meta) as any,
    tile_w: Number(row?.tile_w) || 3,
    tile_h: Number(row?.tile_h) || 1,
    sort_order: Number(row?.sort_order) || 0,
  };
}

function usePublicProducts() {
  return useQuery({
    queryKey: ["public-products"],
    queryFn: async () => {
      const rows = await api.get<any[]>("/products");
      return rows.map(normProduct).filter((p) => p.published && p.is_public);
    },
  });
}

/* ============================================================ Brand logo */

function BrandLogo() {
  const url = useSiteText("brand.logo.url", "");
  if (url) {
    return (
      <div className="grid h-8 w-8 place-items-center overflow-hidden rounded-md bg-primary/10">
        <img src={url} alt="Logo" className="h-full w-full object-contain" />
      </div>
    );
  }
  return (
    <div className="grid h-8 w-8 place-items-center rounded-md bg-primary/15 text-primary">
      <Sparkles className="h-4 w-4" />
    </div>
  );
}

/* ============================================================ Home */

interface PublicNavProps {
  isAdmin: boolean;
  onGoAdmin: () => void;
  onGoLogin: () => void;
}

export function PublicHomePage({ isAdmin, onGoAdmin, onGoLogin }: PublicNavProps) {
  const highlights: { icon: typeof Hammer; key: string; def: string }[] = [
    { icon: Hammer, key: "home.highlight.1", def: "Vũ khí cosplay" },
    { icon: Wand2,  key: "home.highlight.2", def: "Phụ kiện & figure" },
    { icon: Shirt,  key: "home.highlight.3", def: "Trang phục" },
    { icon: Palette,key: "home.highlight.4", def: "Sơn & vẽ tay" },
  ];
  const bgDesktop = useSiteText("home.bg.desktop.url", "");
  const bgMobile = useSiteText("home.bg.mobile.url", "");
  const overlayRaw = useSiteText("home.bg.overlay", "45");
  const hasBg = !!(bgDesktop || bgMobile);
  const overlayPct = (() => {
    const n = parseInt(overlayRaw, 10);
    if (Number.isNaN(n)) return 45;
    return Math.max(0, Math.min(80, n));
  })();

  function go(href: string) {
    window.history.pushState({}, "", href);
    window.dispatchEvent(new PopStateEvent("popstate"));
  }

  return (
    <div className="h-screen flex flex-col">
      {/* Ảnh nền trang chủ — desktop + mobile lưu riêng trong site_texts */}
      {bgDesktop ? (
        <div
          aria-hidden
          className="pointer-events-none fixed inset-0 -z-20 hidden bg-cover bg-center md:block"
          style={{ backgroundImage: `url("${bgDesktop}")` }}
        />
      ) : null}
      {bgMobile ? (
        <div
          aria-hidden
          className="pointer-events-none fixed inset-0 -z-20 block bg-cover bg-center md:hidden"
          style={{ backgroundImage: `url("${bgMobile}")` }}
        />
      ) : null}
      {hasBg ? (
        <div
          aria-hidden
          className="pointer-events-none fixed inset-0 -z-10 backdrop-blur-[2px]"
          style={{
            background: `linear-gradient(to bottom, color-mix(in oklab, var(--background) ${overlayPct + 10}%, transparent), color-mix(in oklab, var(--background) ${overlayPct - 10}%, transparent), color-mix(in oklab, var(--background) ${overlayPct + 25}%, transparent))`,
          }}
        />
      ) : null}
      {/* Header */}
      <header className="shrink-0 z-30 bg-background/85 backdrop-blur-xl">
        <div className="container mx-auto flex items-center justify-between gap-3 px-4 py-3">
          <a href="/" onClick={(e) => { e.preventDefault(); go("/"); }} className="flex items-center gap-2">
            <BrandLogo />
            <div className="flex flex-col leading-tight">
              <EditableText textKey="brand.name" defaultValue="NANA'S INDUSTRY"
                className="text-sm font-bold tracking-tight" />
              <EditableText textKey="brand.tagline" defaultValue="Cosplay Atelier"
                className="text-[9px] uppercase tracking-[0.25em] text-muted-foreground" />
            </div>
          </a>
          <div className="flex items-center gap-2">
            <button
              onClick={() => go("/timeline")}
              className="inline-flex items-center gap-1.5 rounded-md border border-border/60 bg-card px-3 py-1.5 text-xs font-medium hover:bg-muted"
            >
              <Clock className="h-4 w-4" /> <span className="hidden sm:inline">Timeline</span>
            </button>
            {isAdmin ? (
              <>
                <ThemeSwitcher />
                <EditModeToggle />
                <button onClick={onGoAdmin}
                  className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground hover:opacity-90">
                  <Settings className="h-4 w-4" /> Admin
                </button>
              </>
            ) : (
              <button onClick={onGoLogin}
                className="inline-flex items-center gap-1.5 rounded-md border border-border/60 bg-card px-3 py-1.5 text-xs font-medium hover:bg-muted">
                Đăng nhập
              </button>
            )}
          </div>
        </div>
      </header>

      <div className="flex-1 overflow-y-scroll scroll-smooth snap-y snap-mandatory">
      {/* Hero */}
      <section className="relative overflow-hidden flex items-center h-full snap-start snap-always">
        <div className={`absolute inset-0 -z-10 bg-gradient-to-br from-primary/15 ${hasBg ? "via-transparent to-transparent" : "via-background to-background"}`} />
        <div className="absolute -top-24 -right-24 -z-10 h-72 w-72 rounded-full bg-primary/20 blur-3xl" />
        <div className="container mx-auto px-4 py-10 sm:py-16 max-w-3xl text-center w-full">
          <div className={`inline-flex items-center gap-1.5 rounded-full border border-border/60 px-3 py-1 text-[10px] uppercase tracking-[0.25em] text-muted-foreground backdrop-blur ${hasBg ? "bg-card/85" : "bg-card/60"}`}>
            <Sparkles className="h-3 w-3 text-primary" />
            <EditableText textKey="home.badge" defaultValue="Handmade in Vietnam" />
          </div>
          <EditableText as="h1" textKey="home.hero.title"
            defaultValue="Workshop cosplay handmade theo yêu cầu"
            className={`mt-5 text-3xl sm:text-5xl font-bold tracking-tight leading-tight ${hasBg ? "[text-shadow:0_2px_8px_rgba(0,0,0,0.55)]" : ""}`} />
          <EditableText as="p" multiline textKey="home.hero.desc"
            defaultValue="Mình nhận làm vũ khí, phụ kiện, figure và trang phục cosplay cho nhân vật bạn yêu thích — từ phác thảo đến thành phẩm. Ngoài ra còn có kho đồ sẵn để cho thuê nhanh cho event, photoshoot."
            className={`mt-4 text-sm sm:text-base text-muted-foreground leading-relaxed block ${hasBg ? "[text-shadow:0_1px_4px_rgba(0,0,0,0.45)]" : ""}`} />
          <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
            <a href={MESSENGER_URL} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-md bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground shadow-[var(--glow-pink)] hover:opacity-90">
              <MessageCircle className="h-4 w-4" />
              <EditableText textKey="home.cta.messenger" defaultValue="Nhắn Messenger báo giá" />
            </a>
          </div>
          <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-3 text-left">
            {highlights.map(({ icon: Icon, key, def }) => (
              <div key={key} className={`flex items-center gap-2 rounded-lg border border-border/60 px-3 py-2 text-xs sm:text-sm backdrop-blur ${hasBg ? "bg-card/85" : "bg-card/60"}`}>
                <Icon className="h-4 w-4 text-primary shrink-0" />
                <EditableText textKey={key} defaultValue={def} className="truncate" />
              </div>
            ))}
          </div>
        </div>
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 text-[10px] uppercase tracking-[0.25em] text-muted-foreground animate-bounce pointer-events-none">
          <span>Kéo xuống</span>
          <ChevronDown className="h-4 w-4 text-primary" />
        </div>
      </section>

      {/* Collections */}
      <section className="h-full flex items-center snap-start snap-always">
        <div className="container mx-auto px-4 py-10 sm:py-14 w-full">
          <div className="mx-auto max-w-2xl text-center mb-8">
            <EditableText textKey="home.section.eyebrow" defaultValue="Tất cả dịch vụ"
              className="text-[10px] uppercase tracking-[0.25em] text-primary/80 block" />
            <EditableText as="h2" textKey="home.section.title" defaultValue="Bạn cần gì hôm nay?"
              className={`mt-2 text-2xl sm:text-3xl font-bold ${hasBg ? "[text-shadow:0_2px_8px_rgba(0,0,0,0.55)]" : ""}`} />
            <EditableText as="p" multiline textKey="home.section.desc"
              defaultValue="Cho thuê, đặt làm, order và album ảnh chụp — tất cả ở chung một trang để dễ xem và lọc nhanh."
              className={`mt-2 text-sm text-muted-foreground block ${hasBg ? "[text-shadow:0_1px_4px_rgba(0,0,0,0.45)]" : ""}`} />
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            {(
              [
                { mode: "craft",  icon: Scissors,eKey: "home.card.craft",  title: "Đồ đặt làm",  desc: "Handmade theo yêu cầu — gửi nhân vật, mình báo giá." },
                { mode: "rental", icon: Shirt,   eKey: "home.card.rental", title: "Đồ cho thuê", desc: "Trang phục & phụ kiện sẵn có. Thuê nhanh, giá mềm." },
                { mode: "sample", icon: Camera,  eKey: "home.photo.card",  title: "Sample / Ảnh chụp", desc: "Album các buổi chụp đã thực hiện.", toggleKey: "photo" },
                { mode: "order",  icon: Package, eKey: "home.order.card",  title: "Order đồ",     desc: "Catalog hàng order sẵn link từ Taobao, Amazon...", toggleKey: "order" },
              ] as const
            ).map((c) => (
              <CollectionCard key={c.mode} {...c} onGo={go} />
            ))}
          </div>
        </div>
      </section>

      <footer className={`border-t border-border/60 mt-4 py-6 text-center text-xs text-muted-foreground ${hasBg ? "bg-background/70 backdrop-blur" : ""}`}>
        © {new Date().getFullYear()}{" "}
        <EditableText textKey="footer.copy" defaultValue="Nana's Industry · Handmade in Vietnam" />
      </footer>

      <a href={MESSENGER_URL} target="_blank" rel="noreferrer"
        className="fixed bottom-5 right-5 z-40 flex items-center gap-2 rounded-full bg-primary px-4 py-3 text-sm font-semibold text-primary-foreground shadow-[var(--glow-pink)] transition-transform hover:scale-105"
        aria-label="Nhắn tin Messenger">
        <MessageCircle className="h-5 w-5" />
        <span className="hidden sm:inline">Nhắn ngay</span>
      </a>

      <StickerCanvas pageKey="home" />
      </div>
    </div>
  );
}

function CollectionCard({
  mode, icon: Icon, eKey, title, desc, toggleKey, onGo,
}: {
  mode: ProductMode; icon: typeof Hammer; eKey: string; title: string; desc: string;
  toggleKey?: "photo" | "order"; onGo: (href: string) => void;
}) {
  const { editMode, setLocal } = useEditMode();
  const flagKey = `flag.section.${toggleKey ?? mode}`;
  const flagValue = useSiteText(flagKey, "1");
  const enabled = !toggleKey || flagValue === "1";
  if (!enabled && !editMode) return null;

  async function toggleFlag(e: React.MouseEvent) {
    e.preventDefault(); e.stopPropagation();
    const next = enabled ? "0" : "1";
    setLocal(flagKey, next);
    try { await upsertSiteText(flagKey, next); }
    catch (err) { console.error("[toggle flag]", err); }
  }

  return (
    <article className={`relative min-h-[172px] sm:min-h-[190px] ${!enabled ? "opacity-55" : ""}`}>
      <a
        href={enabled ? `/bo-suu-tap/${mode}` : undefined}
        onClick={(e) => {
          e.preventDefault();
          if (enabled) onGo(`/bo-suu-tap/${mode}`);
        }}
        className="group flex h-full flex-col justify-between overflow-hidden rounded-xl border border-border/60 bg-gradient-to-br from-primary/15 via-card to-card p-5 sm:p-7 glow-on-hover"
      >
        <div>
          <Icon className="h-7 w-7 text-primary" />
          <EditableText as="h3" textKey={`${eKey}.title`} defaultValue={title}
            className="mt-4 block text-xl sm:text-2xl font-bold" />
          <EditableText as="p" multiline textKey={`${eKey}.desc`} defaultValue={desc}
            className="mt-2 max-w-md text-sm text-muted-foreground leading-relaxed block" />
        </div>
        {enabled ? (
          <div className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-primary">
            <EditableText textKey={`${eKey}.cta`} defaultValue="Xem ngay" />
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </div>
        ) : (
          <div className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold uppercase tracking-[0.25em] text-white/50">
            Coming soon
          </div>
        )}
        <Icon className="absolute -right-4 -bottom-5 h-24 w-24 text-primary/10 transition-transform group-hover:scale-110" />
      </a>

      {editMode && toggleKey && (
        <button
          onClick={toggleFlag}
          data-edit-keep
          className={`absolute right-3 top-3 z-10 rounded-full px-3 py-1 text-[11px] font-semibold shadow-lg backdrop-blur transition ${enabled ? "bg-white/90 text-black hover:bg-white" : "bg-black/80 text-white hover:bg-black"}`}
          title={enabled ? "Tắt mục này (hiện Coming soon)" : "Bật mục này"}>
          {enabled ? "Đang bật · Tắt" : "Coming soon · Bật"}
        </button>
      )}
    </article>
  );
}


/* ============================================================ Collection */

const TITLES: Record<string, string> = {
  rental: "Đồ cho thuê",
  craft: "Đồ đặt làm",
  sample: "Sample / Ảnh chụp",
  order: "Order đồ",
};

type ViewerVariant = "rental" | "craft" | "sample" | "order";

function goNav(href: string) {
  window.history.pushState({}, "", href);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

interface ViewProps {
  products: Product[];
  loading: boolean;
  onOpen: (p: Product, list: Product[]) => void;
}

export function PublicCollectionPage({ mode }: { mode: string }) {
  const modeTyped = mode as ProductMode;
  const { data: allProducts = [], isLoading: loading } = usePublicProducts();
  const products = useMemo<Product[]>(
    () => allProducts.filter((p) => p.mode === modeTyped || p.mode === "both"),
    [allProducts, modeTyped]
  );

  useEffect(() => {
    if (mode === "all") {
      window.history.replaceState({}, "", "/");
      window.dispatchEvent(new PopStateEvent("popstate"));
    }
  }, [mode]);

  // Shared viewer state
  const [selected, setSelected] = useState<Product | null>(null);
  const [imageIdx, setImageIdx] = useState(0);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const [viewerList, setViewerList] = useState<Product[]>([]);

  function openProduct(p: Product, list: Product[]) {
    setViewerList(list);
    setSelected(p);
    setImageIdx(0);
    setDetailsOpen(false);
  }
  function closeModal() {
    setSelected(null);
    setImageIdx(0);
    setDetailsOpen(false);
  }

  const images = selected
    ? (selected.images.length ? selected.images : selected.cover_image ? [selected.cover_image] : [])
    : [];
  const title = TITLES[mode] ?? MODE_LABEL[mode] ?? "Bộ sưu tập";
  const variant: ViewerVariant = (["rental", "craft", "sample", "order"].includes(mode) ? mode : "rental") as ViewerVariant;

  const shared: ViewProps = { products, loading, onOpen: openProduct };
  const darkChrome = mode === "craft" || mode === "sample";

  return (
    <div className={`min-h-screen ${darkChrome ? "bg-black text-white" : ""}`}>
      <CollectionHeader title={title} mode={mode} dark={darkChrome} />

      {mode === "rental" && <RentalView {...shared} />}
      {mode === "craft" && <CraftView {...shared} />}
      {mode === "sample" && <SampleView {...shared} />}
      {mode === "order" && <OrderView {...shared} />}

      <MessengerFab />

      {selected && (
        <ProductViewer
          variant={variant}
          selected={selected}
          images={images}
          imageIdx={imageIdx}
          setImageIdx={setImageIdx}
          filtered={viewerList}
          setSelected={setSelected}
          detailsOpen={detailsOpen}
          setDetailsOpen={setDetailsOpen}
          showHint={showHint}
          setShowHint={setShowHint}
          onClose={closeModal}
        />
      )}
    </div>
  );
}

/* ---- shared chrome ---- */

/* ---- Showcase strip (admin-curated or random/3 days) ---- */

type ShowcaseCfg = { mode: "manual" | "random" | "off"; ids: string[]; count: number };
const SHOWCASE_DEFAULT: ShowcaseCfg = { mode: "random", ids: [], count: 6 };
const SHOWCASE_BUCKET_MS = 3 * 24 * 60 * 60 * 1000;

function parseShowcaseCfg(raw: string): ShowcaseCfg {
  try {
    const o = raw ? JSON.parse(raw) : null;
    if (!o || typeof o !== "object") return { ...SHOWCASE_DEFAULT };
    const m = o.mode === "manual" || o.mode === "off" ? o.mode : "random";
    const ids = Array.isArray(o.ids) ? o.ids.filter((x: any) => typeof x === "string") : [];
    const count = Math.max(2, Math.min(12, Number(o.count) || 6));
    return { mode: m, ids, count };
  } catch { return { ...SHOWCASE_DEFAULT }; }
}

function seededShuffle<T>(arr: T[], seed: number): T[] {
  const a = arr.slice();
  let s = seed >>> 0;
  for (let i = a.length - 1; i > 0; i--) {
    s = (s * 1664525 + 1013904223) >>> 0;
    const j = s % (i + 1);
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function CollectionShowcase({ mode, products, dark }: { mode: ProductMode; products: Product[]; dark: boolean }) {
  const { editMode } = useEditMode();
  const cfgRaw = useSiteText(`showcase.${mode}`, "");
  const cfg = useMemo(() => parseShowcaseCfg(cfgRaw), [cfgRaw]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const candidates = useMemo(
    () => products.filter((p) => (p.cover_image && p.cover_image.trim()) || (p.images && p.images[0])),
    [products],
  );

  const items = useMemo<Product[]>(() => {
    if (cfg.mode === "off") return [];
    if (cfg.mode === "manual") {
      const bySlug = new Map(candidates.map((p) => [p.slug, p] as const));
      const byId = new Map(candidates.map((p) => [p.id, p] as const));
      const seen = new Set<string>();
      const out: Product[] = [];
      for (const key of cfg.ids) {
        const p = bySlug.get(key) ?? byId.get(key);
        if (p && !seen.has(p.id)) { seen.add(p.id); out.push(p); }
      }
      return out;
    }
    const bucket = Math.floor(Date.now() / SHOWCASE_BUCKET_MS);
    const modeSeed = mode.split("").reduce((a, c) => (a * 31 + c.charCodeAt(0)) >>> 0, 7);
    const seed = (bucket * 2654435761 + modeSeed) >>> 0;
    return seededShuffle(candidates, seed).slice(0, cfg.count);
  }, [cfg, candidates, mode]);

  async function saveCfg(next: ShowcaseCfg) {
    setSaving(true);
    try { await upsertSiteText(`showcase.${mode}`, JSON.stringify(next)); }
    catch (e) { console.error("[showcase save]", e); }
    finally { setSaving(false); }
  }

  // Hide entirely if not in edit mode and nothing to show
  if (!editMode && (cfg.mode === "off" || items.length === 0)) return null;

  const toolbarBg = dark
    ? "border-white/15 bg-white/5 text-white"
    : "border-primary/40 bg-primary/5";

  return (
    <section className="container mx-auto px-4 pt-3">
      {editMode ? (
        <div className={`mb-3 flex flex-wrap items-center gap-2 rounded-md border border-dashed px-3 py-2 text-xs ${toolbarBg}`}>
          <span className="font-semibold inline-flex items-center gap-1">
            <Star className="h-3.5 w-3.5" /> Showcase đầu trang
          </span>
          <span className="opacity-50">·</span>
          <button
            type="button"
            onClick={() => setPickerOpen(true)}
            className="inline-flex items-center gap-1 rounded bg-primary/15 px-2 py-1 font-medium text-primary hover:bg-primary/25"
          >
            <ImagePlus className="h-3 w-3" /> Chọn ảnh
            {cfg.mode === "manual" ? <span className="ml-1 opacity-70">({cfg.ids.length})</span> : null}
          </button>
          <button
            type="button"
            onClick={() => saveCfg({ ...cfg, mode: "random" })}
            disabled={saving}
            className={`inline-flex items-center gap-1 rounded px-2 py-1 font-medium transition ${
              cfg.mode === "random" ? "bg-primary text-primary-foreground" : "bg-muted hover:bg-muted/70"
            }`}
            title="Tự pick ngẫu nhiên, đổi sau mỗi 3 ngày"
          >
            <Shuffle className="h-3 w-3" /> Random · 3 ngày/lần
          </button>
          <button
            type="button"
            onClick={() => saveCfg({ ...cfg, mode: "off" })}
            disabled={saving}
            className={`inline-flex items-center gap-1 rounded px-2 py-1 font-medium transition ${
              cfg.mode === "off" ? "bg-destructive text-destructive-foreground" : "bg-muted hover:bg-muted/70"
            }`}
          >
            <EyeOff className="h-3 w-3" /> Ẩn
          </button>
          <label className="ml-auto inline-flex items-center gap-1">
            <span className="opacity-70">Số ảnh:</span>
            <select
              value={cfg.count}
              onChange={(e) => saveCfg({ ...cfg, count: Number(e.target.value) })}
              className="rounded border border-border bg-background px-1 py-0.5 text-xs text-foreground"
            >
              {[3, 4, 5, 6, 8, 10, 12].map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </label>
        </div>
      ) : null}

      {items.length > 0 ? (
        <ShowcaseGrid items={items} />
      ) : editMode ? (
        <div className={`rounded-md border border-dashed p-6 text-center text-xs ${dark ? "border-white/15 text-white/60" : "border-border/60 text-muted-foreground"}`}>
          Chưa có ảnh để showcase.
          {cfg.mode === "manual"
            ? ' Bấm "Chọn ảnh" để chọn từ thư mục bộ sưu tập này.'
            : " Hãy đăng sản phẩm có ảnh đại diện trong mục Bộ sưu tập."}
        </div>
      ) : null}

      {pickerOpen ? (
        <ShowcasePicker
          mode={mode}
          products={candidates}
          selected={cfg.ids}
          onClose={() => setPickerOpen(false)}
          onSave={(ids) => { void saveCfg({ ...cfg, mode: "manual", ids }); setPickerOpen(false); }}
        />
      ) : null}
    </section>
  );
}

function ShowcaseGrid({ items }: { items: Product[] }) {
  return (
    <div className="mb-2 columns-2 gap-2 sm:columns-3 md:columns-4 lg:columns-5">
      {items.map((p) => {
        const url = (p.cover_image && p.cover_image.trim()) || p.images[0];
        if (!url) return null;
        return (
          <a
            key={p.id}
            href={`/bo-suu-tap/${p.mode === "both" ? "rental" : p.mode}#${p.slug}`}
            onClick={(e) => { e.preventDefault(); }}
            className="mb-2 block break-inside-avoid overflow-hidden rounded-md bg-muted shadow-sm"
            title={p.name}
          >
            <img
              src={url}
              alt={p.name}
              loading="lazy"
              className="block h-auto w-full"
            />
          </a>
        );
      })}
    </div>
  );
}

function ShowcasePicker({
  mode, products, selected, onClose, onSave,
}: {
  mode: ProductMode;
  products: Product[];
  selected: string[];
  onClose: () => void;
  onSave: (ids: string[]) => void;
}) {
  const [picked, setPicked] = useState<string[]>(() => selected.slice());
  const [q, setQ] = useState("");

  // Sort: newest first (sort_order DESC then by name)
  const list = useMemo(() => {
    const filtered = products.filter((p) => !q || matchesQuery(p, q));
    return filtered.slice().sort((a, b) => (b.sort_order ?? 0) - (a.sort_order ?? 0));
  }, [products, q]);

  function toggle(slug: string) {
    setPicked((prev) => prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug]);
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto bg-black/70 p-4" onClick={onClose}>
      <div className="mt-8 w-full max-w-4xl rounded-lg border border-border bg-background p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="mb-3 flex items-center justify-between gap-3">
          <div>
            <h3 className="text-base font-semibold">Chọn ảnh đại diện để showcase — {MODE_LABEL[mode] ?? mode}</h3>
            <p className="text-xs text-muted-foreground">Click để chọn nhiều ảnh. Thứ tự click = thứ tự hiển thị.</p>
          </div>
          <button onClick={onClose} className="rounded p-1 hover:bg-muted"><X className="h-4 w-4" /></button>
        </div>

        <div className="mb-3 flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Tìm tên, nhân vật, fandom…"
              className="h-9 w-full rounded-md border border-border bg-background pl-8 pr-3 text-sm"
            />
          </div>
          <span className="text-xs text-muted-foreground">{picked.length} đã chọn / {list.length}</span>
        </div>

        <div className="grid max-h-[60vh] grid-cols-3 gap-2 overflow-y-auto sm:grid-cols-4 md:grid-cols-5">
          {list.length === 0 ? (
            <div className="col-span-full p-10 text-center text-sm text-muted-foreground">Không có sản phẩm nào.</div>
          ) : list.map((p) => {
            const url = (p.cover_image && p.cover_image.trim()) || p.images[0];
            const isSel = picked.includes(p.slug);
            const order = isSel ? picked.indexOf(p.slug) + 1 : null;
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => toggle(p.slug)}
                className={`group relative aspect-[3/4] overflow-hidden rounded-md border bg-muted ${
                  isSel ? "border-primary ring-2 ring-primary" : "border-border hover:border-primary"
                }`}
                title={p.name}
              >
                {url ? (
                  <img src={url} alt={p.name} loading="lazy" className="h-full w-full object-cover" />
                ) : (
                  <div className="grid h-full place-items-center text-xs text-muted-foreground">Chưa có ảnh</div>
                )}
                {order ? (
                  <span className="absolute right-1 top-1 grid h-5 w-5 place-items-center rounded-full bg-primary text-[11px] font-bold text-primary-foreground">{order}</span>
                ) : null}
                <div className="absolute inset-x-0 bottom-0 truncate bg-black/60 px-1 py-0.5 text-[10px] text-white">{p.name}</div>
              </button>
            );
          })}
        </div>

        <div className="mt-4 flex justify-end gap-2">
          <button onClick={() => setPicked([])} className="rounded border border-border px-3 py-1.5 text-xs hover:bg-muted">Bỏ chọn tất cả</button>
          <button onClick={onClose} className="rounded border border-border px-3 py-1.5 text-xs hover:bg-muted">Huỷ</button>
          <button onClick={() => onSave(picked)} className="rounded bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground hover:opacity-90">
            Lưu showcase
          </button>
        </div>
      </div>
    </div>
  );
}

function CollectionHeader({ title, mode, dark }: { title: string; mode: string; dark: boolean }) {
  const bg = dark
    ? "border-white/10 bg-black/80 backdrop-blur-xl"
    : "border-border/60 bg-background/85 backdrop-blur-xl";
  const linkClass = dark
    ? "text-white/60 hover:text-white"
    : "text-muted-foreground hover:text-foreground";
  const divider = dark ? "bg-white/15" : "bg-border";
  return (
    <header className={`sticky top-0 z-30 border-b ${bg}`}>
      <div className="container mx-auto flex items-center gap-2 px-4 py-3">
        <a href="/" onClick={(e) => { e.preventDefault(); goNav("/"); }}
          className={`flex items-center gap-1.5 text-sm ${linkClass}`}>
          <ArrowLeft className="h-4 w-4" /> <span className="hidden sm:inline">Trang chủ</span>
        </a>
        <div className={`mx-2 h-5 w-px ${divider}`} />
        <EditableText as="h1" textKey={`collection.${mode}.title`} defaultValue={title}
          className="text-sm sm:text-base font-bold tracking-tight" />
        <div className="ml-auto flex items-center gap-2">
          <EditModeToggle />
          <a href={MESSENGER_URL} target="_blank" rel="noreferrer">
            <span className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground shadow-[var(--glow-pink)]">
              <MessageCircle className="h-4 w-4" /> <span className="hidden sm:inline">Nhắn đặt</span>
            </span>
          </a>
        </div>
      </div>
    </header>
  );
}

function MessengerFab() {
  return (
    <a href={MESSENGER_URL} target="_blank" rel="noreferrer"
      className="fixed bottom-5 right-5 z-40 flex items-center gap-2 rounded-full bg-primary px-4 py-3 text-sm font-semibold text-primary-foreground shadow-[var(--glow-pink)] transition-transform hover:scale-105">
      <MessageCircle className="h-5 w-5" />
      <span className="hidden sm:inline">Nhắn ngay</span>
    </a>
  );
}

function EmptyState({ msg, dark }: { msg: string; dark?: boolean }) {
  return (
    <div className={`rounded-xl border border-dashed p-16 text-center ${dark ? "border-white/10 text-white/50" : "border-border/60 text-muted-foreground"}`}>
      <Sparkles className={`mx-auto h-10 w-10 opacity-40 ${dark ? "text-white/40" : "text-muted-foreground"}`} />
      <p className="mt-4">{msg}</p>
    </div>
  );
}

function SkeletonGrid({ cols, dark }: { cols: string; dark?: boolean }) {
  return (
    <div className={`grid gap-3 ${cols}`}>
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i} className={`aspect-[3/4] animate-pulse rounded-lg ${dark ? "bg-white/5" : "bg-card"}`} />
      ))}
    </div>
  );
}

function matchesQuery(p: Product, q: string): boolean {
  if (!q) return true;
  const hay = [p.name, p.fandom, p.character_name, p.category, p.materials, p.description,
    ...Object.values(p.attributes ?? {})]
    .filter(Boolean).map((v) => normalize(String(v))).join(" ");
  return hay.includes(normalize(q));
}

function Chip({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button onClick={onClick}
      className={`rounded-full border px-3 py-1.5 text-xs transition ${active ? "border-primary bg-primary text-primary-foreground shadow-[var(--glow-pink)]" : "border-border/60 bg-card hover:bg-muted"}`}>
      {children}
    </button>
  );
}

/* ---- 1) Rental — Lọc nhanh, mật độ cao ---- */

function RentalView({ products, loading, onOpen }: ViewProps) {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState<string>("all");

  const categories = useMemo(() => {
    const map = new Map<string, number>();
    for (const p of products) { const c = p.category ?? "Khác"; map.set(c, (map.get(c) ?? 0) + 1); }
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [products]);

  const filtered = useMemo(() => {
    return products.filter((p) =>
      (category === "all" || (p.category ?? "Khác") === category) && matchesQuery(p, query)
    );
  }, [products, category, query]);

  return (
    <div className="container mx-auto px-4 py-4">
      <div className="relative mb-3 max-w-md">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
        <input value={query} onChange={(e) => setQuery(e.target.value)}
          placeholder="Tìm nhân vật, fandom, kiểu đồ..."
          className="h-9 w-full rounded-md border border-border/60 bg-background pl-8 pr-8 text-sm" />
        {query && (
          <button onClick={() => setQuery("")} aria-label="Xoá tìm kiếm"
            className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:bg-muted">
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      <div className="-mx-4 mb-4 overflow-x-auto px-4 pb-1">
        <div className="flex gap-1.5 whitespace-nowrap">
          <Chip active={category === "all"} onClick={() => setCategory("all")}>
            Tất cả <span className="opacity-60">({products.length})</span>
          </Chip>
          {categories.map(([c, n]) => (
            <Chip key={c} active={category === c} onClick={() => setCategory(c)}>
              {c} <span className="opacity-60">({n})</span>
            </Chip>
          ))}
        </div>
      </div>

      <div className="mb-2 text-xs text-muted-foreground">{filtered.length} bộ</div>

      {loading ? (
        <SkeletonGrid cols="grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5" />
      ) : filtered.length === 0 ? (
        <EmptyState msg="Chưa có bộ nào trong mục này." />
      ) : (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 sm:gap-3 lg:grid-cols-4 xl:grid-cols-5">
          {filtered.map((p) => {
            const cover = p.cover_image ?? p.images[0];
            const sub = [p.character_name, p.fandom].filter(Boolean).join(" · ");
            return (
              <button key={p.id} onClick={() => onOpen(p, filtered)}
                className="group block overflow-hidden rounded-md bg-card text-left shadow-sm transition hover:shadow-[var(--glow-pink)]">
                <div className="relative aspect-[3/4] overflow-hidden bg-muted">
                  {cover ? (
                    <img src={cover} alt={p.name} loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  ) : (
                    <div className="grid h-full place-items-center text-muted-foreground">
                      <Sparkles className="h-6 w-6 opacity-40" />
                    </div>
                  )}
                  {p.attributes?.size && (
                    <span className="absolute right-1.5 top-1.5 rounded bg-black/55 px-1.5 py-0.5 text-[10px] font-medium text-white backdrop-blur">
                      {p.attributes.size}
                    </span>
                  )}
                  <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent p-2 pt-8">
                    <div className="line-clamp-1 text-[12px] font-medium text-white">{p.name}</div>
                    {sub && <div className="line-clamp-1 text-[10px] text-white/70">{sub}</div>}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ---- 2) Craft — Editorial, asymmetric ---- */

function CraftTab({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button onClick={onClick}
      className={`whitespace-nowrap border-b-2 pb-1.5 transition ${active ? "border-primary text-white" : "border-transparent text-white/50 hover:text-white"}`}>
      {children}
    </button>
  );
}

function CraftView({ products, loading, onOpen }: ViewProps) {
  const { editMode } = useEditMode();
  const [category, setCategory] = useState("all");
  const categories = useMemo(() => {
    const set = new Set<string>();
    for (const p of products) if (p.category) set.add(p.category);
    return Array.from(set).sort();
  }, [products]);
  const sorted = useMemo(
    () => [...products].sort((a, b) => (b.sort_order ?? 0) - (a.sort_order ?? 0)),
    [products]
  );
  const filtered = useMemo(
    () => sorted.filter((p) => category === "all" || (p.category ?? "") === category),
    [sorted, category]
  );

  const [localOverride, setLocalOverride] = useState<Record<string, { tile_w?: number; tile_h?: number; sort_order?: number }>>({});
  const view = filtered.map((p) => ({ ...p, ...(localOverride[p.id] ?? {}) }))
    .sort((a, b) => (b.sort_order ?? 0) - (a.sort_order ?? 0));

  async function persist(id: string, patch: { tile_w?: number; tile_h?: number; sort_order?: number }) {
    setLocalOverride((m) => ({ ...m, [id]: { ...(m[id] ?? {}), ...patch } }));
    try { await api.put(`/products/${encodeURIComponent(id)}`, patch); } catch (e) { console.error("[craft tile]", e); }
  }
  function resizeTile(id: string, patch: { tile_w?: number; tile_h?: number }) { persist(id, patch); }
  function moveTile(index: number, dir: -1 | 1) {
    const a = view[index]; const b = view[index + dir];
    if (!a || !b) return;
    const sa = a.sort_order ?? 0; const sb = b.sort_order ?? 0;
    const newSa = sb === sa ? sa + dir * 1 : sb;
    const newSb = sb === sa ? sa : sa;
    persist(a.id, { sort_order: newSa });
    persist(b.id, { sort_order: newSb });
  }

  return (
    <div className="relative bg-[#0c0a0c]">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-[420px] bg-[radial-gradient(ellipse_at_top,_color-mix(in_oklab,_var(--primary)_14%,_transparent),_transparent_70%)]" />

      {/* Compact 1-row hero */}
      <section className="relative border-b border-white/10 px-4 py-5 sm:py-7">
        <div className="container mx-auto flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.3em] text-primary/80">Đặt làm thủ công</div>
            <EditableText as="h2" textKey="collection.craft.hero"
              defaultValue="Mỗi món là một tác phẩm"
              className="mt-1 font-serif text-2xl sm:text-4xl font-bold leading-tight" />
            <EditableText as="p" multiline textKey="collection.craft.sub"
              defaultValue="Vũ khí, phụ kiện và trang phục handmade theo nhân vật bạn yêu — chất lượng đặt lên hàng đầu."
              className="mt-1 text-xs sm:text-sm text-white/55 leading-relaxed block max-w-2xl" />
          </div>
          <a href={MESSENGER_URL} target="_blank" rel="noreferrer"
            className="shrink-0 inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground shadow-[var(--glow-pink)]">
            <MessageCircle className="h-4 w-4" /> Đặt một món
          </a>
        </div>
      </section>

      {categories.length > 0 && (
        <div className="relative border-b border-white/10">
          <div className="container mx-auto flex gap-6 overflow-x-auto px-4 py-3 text-sm">
            <CraftTab active={category === "all"} onClick={() => setCategory("all")}>Tất cả</CraftTab>
            {categories.map((c) => (
              <CraftTab key={c} active={category === c} onClick={() => setCategory(c)}>{c}</CraftTab>
            ))}
          </div>
        </div>
      )}

      {editMode && (
        <div className="border-b border-white/10 bg-white/5 px-4 py-2 text-[11px] text-white/60">
          Edit mode: bấm S / M / L / XL để chỉnh rộng ô · ▭/▯ đổi chiều cao · ←/→ đổi thứ tự.
        </div>
      )}

      <div className="relative container mx-auto px-2 py-4 sm:px-3 sm:py-6">
        {loading ? (
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-6 sm:gap-3 lg:grid-cols-12 lg:gap-4 auto-rows-[220px] sm:auto-rows-[240px] lg:auto-rows-[280px]">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className={`${TILE_W_CLS[4]} ${TILE_H_CLS[1]} animate-pulse bg-white/5 rounded-sm`} />
            ))}
          </div>
        ) : view.length === 0 ? (
          <EmptyState msg="Chưa có tác phẩm trong mục này." dark />
        ) : (
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-6 sm:gap-3 lg:grid-cols-12 lg:gap-4 auto-rows-[220px] sm:auto-rows-[240px] lg:auto-rows-[280px] grid-flow-row-dense">
            {view.map((p, i) => (
              <CraftTile
                key={p.id}
                product={p}
                onOpen={() => onOpen(p, view)}
                onResize={(patch) => resizeTile(p.id, patch)}
                onMove={(dir) => moveTile(i, dir)}
                canMoveLeft={i > 0}
                canMoveRight={i < view.length - 1}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function CraftTile({
  product, onOpen, onResize, onMove, canMoveLeft, canMoveRight,
}: {
  product: Product;
  onOpen: () => void;
  onResize: (patch: { tile_w?: number; tile_h?: number }) => void;
  onMove: (dir: -1 | 1) => void;
  canMoveLeft: boolean;
  canMoveRight: boolean;
}) {
  const { editMode } = useEditMode();
  const w = Number(product.tile_w) || 4;
  const h = Number(product.tile_h) || 1;
  const cover = product.cover_image ?? product.images[0];

  return (
    <div className={`${TILE_W_CLS[w] ?? TILE_W_CLS[4]} ${TILE_H_CLS[h] ?? TILE_H_CLS[1]} group relative overflow-hidden rounded-sm border border-white/5 bg-white/5 transition hover:border-primary/30 hover:ring-1 hover:ring-primary/20`}>
      <button onClick={onOpen} className="block h-full w-full text-left">
        {cover ? (
          <img src={cover} alt={product.name} loading="lazy"
            className="h-full w-full object-cover transition-all duration-700 group-hover:brightness-110 group-hover:scale-[1.015]" />
        ) : (
          <div className="grid h-full w-full place-items-center text-white/30">
            <Sparkles className="h-8 w-8" />
          </div>
        )}
        <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 via-black/40 to-transparent p-4 sm:p-5">
          <h3 className="font-serif text-base sm:text-xl leading-snug text-white drop-shadow line-clamp-2">{product.name}</h3>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px]">
            {product.materials && <span className="text-white/65 line-clamp-1">{product.materials}</span>}
            {product.price && <span className="text-primary/95">{product.price}</span>}
          </div>
        </div>
      </button>

      {editMode && (
        <div className="absolute inset-x-1 top-1 z-10 flex flex-wrap items-center gap-1 rounded bg-black/85 p-1 text-[10px] text-white shadow-lg backdrop-blur">
          <span className="px-1 opacity-60">size</span>
          {TILE_W_OPTIONS.map((o) => (
            <button key={o.w}
              onClick={(e) => { e.stopPropagation(); onResize({ tile_w: o.w }); }}
              className={`rounded px-1.5 py-0.5 ${w === o.w ? "bg-white text-black" : "bg-white/10 hover:bg-white/25"}`}
              title={`Rộng ${o.w}/12`}>
              {o.label}
            </button>
          ))}
          <button
            onClick={(e) => { e.stopPropagation(); onResize({ tile_h: h === 1 ? 2 : 1 }); }}
            className={`rounded px-1.5 py-0.5 ${h === 2 ? "bg-white text-black" : "bg-white/10 hover:bg-white/25"}`}
            title="Đổi chiều cao">
            {h === 2 ? "▯" : "▭"}
          </button>
          <span className="mx-1 opacity-40">|</span>
          <button
            disabled={!canMoveLeft}
            onClick={(e) => { e.stopPropagation(); onMove(-1); }}
            className="rounded bg-white/10 px-1.5 py-0.5 hover:bg-white/25 disabled:opacity-30"
            title="Lùi trái">←</button>
          <button
            disabled={!canMoveRight}
            onClick={(e) => { e.stopPropagation(); onMove(1); }}
            className="rounded bg-white/10 px-1.5 py-0.5 hover:bg-white/25 disabled:opacity-30"
            title="Tiến phải">→</button>
        </div>
      )}
    </div>
  );
}

/* ---- 3) Sample — Photographer portfolio ---- */

function SampleTab({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button onClick={onClick}
      className={`whitespace-nowrap transition ${active ? "text-white" : "text-white/30 hover:text-white/70"}`}>
      {children}
    </button>
  );
}

// Static class maps so Tailwind không purge mất col-span / row-span động.
const TILE_W_CLS: Record<number, string> = {
  2: "col-span-1 sm:col-span-2 lg:col-span-2",
  3: "col-span-1 sm:col-span-3 lg:col-span-3",
  4: "col-span-2 sm:col-span-3 lg:col-span-4",
  6: "col-span-2 sm:col-span-6 lg:col-span-6",
};
const TILE_H_CLS: Record<number, string> = {
  1: "row-span-1",
  2: "row-span-2",
};
const TILE_W_OPTIONS: { w: number; label: string }[] = [
  { w: 2, label: "S" }, { w: 3, label: "M" }, { w: 4, label: "L" }, { w: 6, label: "XL" },
];

function SampleTile({
  product, onOpen, onResize, onMove, canMoveLeft, canMoveRight,
}: {
  product: Product;
  onOpen: () => void;
  onResize: (patch: { tile_w?: number; tile_h?: number }) => void;
  onMove: (dir: -1 | 1) => void;
  canMoveLeft: boolean;
  canMoveRight: boolean;
}) {
  const { editMode } = useEditMode();
  const w = Number(product.tile_w) || 3;
  const h = Number(product.tile_h) || 1;
  const cover = product.cover_image ?? product.images[0];
  const sub = [product.character_name, product.fandom].filter(Boolean).join(" · ");

  return (
    <div className={`${TILE_W_CLS[w] ?? TILE_W_CLS[3]} ${TILE_H_CLS[h] ?? TILE_H_CLS[1]} group relative overflow-hidden bg-white/5`}>
      <button onClick={onOpen} className="block h-full w-full text-left">
        {cover ? (
          <img src={cover} alt={product.name} loading="lazy"
            className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]" />
        ) : (
          <div className="grid h-full w-full place-items-center text-xs text-white/40">(no image)</div>
        )}
        {/* Caption overlay — chỉ hiện khi hover, không che ảnh */}
        <div className="pointer-events-none absolute inset-x-0 bottom-0 translate-y-2 bg-gradient-to-t from-black/85 via-black/30 to-transparent p-3 opacity-0 transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100">
          <div className="font-serif text-sm sm:text-base font-light leading-tight text-white line-clamp-2">{product.name}</div>
          {sub && <div className="mt-0.5 text-[9px] uppercase tracking-[0.25em] text-white/60 line-clamp-1">{sub}</div>}
        </div>
      </button>

      {editMode && (
        <div className="absolute inset-x-1 top-1 z-10 flex flex-wrap items-center gap-1 rounded bg-black/85 p-1 text-[10px] text-white shadow-lg backdrop-blur">
          <span className="px-1 opacity-60">size</span>
          {TILE_W_OPTIONS.map((o) => (
            <button key={o.w}
              onClick={(e) => { e.stopPropagation(); onResize({ tile_w: o.w }); }}
              className={`rounded px-1.5 py-0.5 ${w === o.w ? "bg-white text-black" : "bg-white/10 hover:bg-white/25"}`}
              title={`Rộng ${o.w}/12`}>
              {o.label}
            </button>
          ))}
          <button
            onClick={(e) => { e.stopPropagation(); onResize({ tile_h: h === 1 ? 2 : 1 }); }}
            className={`rounded px-1.5 py-0.5 ${h === 2 ? "bg-white text-black" : "bg-white/10 hover:bg-white/25"}`}
            title="Đổi chiều cao">
            {h === 2 ? "▯" : "▭"}
          </button>
          <span className="mx-1 opacity-40">|</span>
          <button
            disabled={!canMoveLeft}
            onClick={(e) => { e.stopPropagation(); onMove(-1); }}
            className="rounded bg-white/10 px-1.5 py-0.5 hover:bg-white/25 disabled:opacity-30"
            title="Lùi trái">←</button>
          <button
            disabled={!canMoveRight}
            onClick={(e) => { e.stopPropagation(); onMove(1); }}
            className="rounded bg-white/10 px-1.5 py-0.5 hover:bg-white/25 disabled:opacity-30"
            title="Tiến phải">→</button>
        </div>
      )}
    </div>
  );
}

function SampleView({ products, loading, onOpen }: ViewProps) {
  const { editMode } = useEditMode();
  const [category, setCategory] = useState("all");
  const categories = useMemo(() => {
    const set = new Set<string>();
    for (const p of products) if (p.category) set.add(p.category);
    return Array.from(set).sort();
  }, [products]);

  // sort theo sort_order DESC (mới/lớn trước), rồi rơi về thứ tự cũ
  const sorted = useMemo(
    () => [...products].sort((a, b) => (b.sort_order ?? 0) - (a.sort_order ?? 0)),
    [products]
  );
  const filtered = useMemo(
    () => sorted.filter((p) => category === "all" || (p.category ?? "") === category),
    [sorted, category]
  );

  // Optimistic patch: cập nhật local list ngay, ghi PHP nền.
  const [localOverride, setLocalOverride] = useState<Record<string, { tile_w?: number; tile_h?: number; sort_order?: number }>>({});
  const view = filtered.map((p) => ({ ...p, ...(localOverride[p.id] ?? {}) }))
    .sort((a, b) => (b.sort_order ?? 0) - (a.sort_order ?? 0));

  async function persist(id: string, patch: { tile_w?: number; tile_h?: number; sort_order?: number }) {
    setLocalOverride((m) => ({ ...m, [id]: { ...(m[id] ?? {}), ...patch } }));
    try { await api.put(`/products/${encodeURIComponent(id)}`, patch); } catch (e) { console.error("[sample tile]", e); }
  }

  function resizeTile(id: string, patch: { tile_w?: number; tile_h?: number }) {
    persist(id, patch);
  }
  function moveTile(index: number, dir: -1 | 1) {
    const a = view[index]; const b = view[index + dir];
    if (!a || !b) return;
    const sa = a.sort_order ?? 0; const sb = b.sort_order ?? 0;
    // swap sort_order — luôn đảm bảo khác nhau để re-render đổi thứ tự
    const newSa = sb === sa ? sa + dir * 1 : sb;
    const newSb = sb === sa ? sa : sa;
    persist(a.id, { sort_order: newSa });
    persist(b.id, { sort_order: newSb });
  }

  return (
    <div>
      {/* Header gọn 1 hàng */}
      <section className="border-b border-white/5 px-4 py-5 sm:py-7">
        <div className="container mx-auto flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between sm:gap-6">
          <div className="min-w-0">
            <EditableText textKey="collection.sample.eyebrow" defaultValue="Portfolio"
              className="text-[10px] uppercase tracking-[0.4em] text-white/40" />
            <EditableText as="h2" textKey="collection.sample.title"
              defaultValue="Những khung hình đã đi qua"
              className="mt-1 font-serif text-2xl sm:text-3xl font-light leading-tight block" />
          </div>
          <EditableText as="p" multiline textKey="collection.sample.intro"
            defaultValue="Mỗi ô là một buổi chụp. Bấm để xem trọn album."
            className="max-w-md text-xs sm:text-sm text-white/50 leading-relaxed block" />
        </div>
      </section>

      {categories.length > 0 && (
        <div className="border-b border-white/5">
          <div className="container mx-auto flex gap-5 overflow-x-auto px-4 py-3 text-xs uppercase tracking-[0.2em]">
            <SampleTab active={category === "all"} onClick={() => setCategory("all")}>All</SampleTab>
            {categories.map((c) => (
              <SampleTab key={c} active={category === c} onClick={() => setCategory(c)}>{c}</SampleTab>
            ))}
          </div>
        </div>
      )}

      {editMode && (
        <div className="border-b border-white/5 bg-white/5 px-4 py-2 text-[11px] text-white/60">
          Edit mode: bấm S / M / L / XL để chỉnh rộng ô · ▭/▯ đổi chiều cao · ←/→ đổi thứ tự.
        </div>
      )}

      <div className="container mx-auto px-2 py-3 sm:px-3 sm:py-4">
        {loading ? (
          <div className="grid grid-cols-2 gap-1 sm:grid-cols-6 sm:gap-2 lg:grid-cols-12 auto-rows-[140px] sm:auto-rows-[150px] lg:auto-rows-[180px]">
            {Array.from({ length: 12 }).map((_, i) => (
              <div key={i} className={`${TILE_W_CLS[3]} ${TILE_H_CLS[1]} animate-pulse bg-white/5`} />
            ))}
          </div>
        ) : view.length === 0 ? (
          <EmptyState msg="Chưa có buổi chụp nào." dark />
        ) : (
          <div className="grid grid-cols-2 gap-1 sm:grid-cols-6 sm:gap-2 lg:grid-cols-12 auto-rows-[140px] sm:auto-rows-[150px] lg:auto-rows-[180px] grid-flow-row-dense">
            {view.map((p, i) => (
              <SampleTile
                key={p.id}
                product={p}
                onOpen={() => onOpen(p, view)}
                onResize={(patch) => resizeTile(p.id, patch)}
                onMove={(dir) => moveTile(i, dir)}
                canMoveLeft={i > 0}
                canMoveRight={i < view.length - 1}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ---- 4) Order — Marketplace 2hand ---- */

function OrderView({ products, loading, onOpen }: ViewProps) {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("all");
  const [status, setStatus] = useState<"all" | "available" | "sold">("all");
  const [sort, setSort] = useState<"new" | "price-asc" | "price-desc">("new");

  const categories = useMemo(() => {
    const map = new Map<string, number>();
    for (const p of products) { const c = p.category ?? "Khác"; map.set(c, (map.get(c) ?? 0) + 1); }
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [products]);

  function isSold(p: Product) {
    const s = (p.attributes?.status ?? "").toLowerCase();
    return s === "sold" || s === "da-ban" || s === "đã bán";
  }
  function isFresh(p: Product) {
    const s = (p.attributes?.status ?? "").toLowerCase();
    return s === "new" || s === "moi-ve" || s === "mới về";
  }
  function priceNum(p: Product) {
    const n = parseInt((p.price ?? "").replace(/[^\d]/g, ""), 10);
    return Number.isFinite(n) ? n : 0;
  }

  const filtered = useMemo(() => {
    let list = products.filter((p) =>
      (category === "all" || (p.category ?? "Khác") === category) &&
      matchesQuery(p, query) &&
      (status === "all" || (status === "sold" ? isSold(p) : !isSold(p)))
    );
    if (sort === "price-asc") list = [...list].sort((a, b) => priceNum(a) - priceNum(b));
    else if (sort === "price-desc") list = [...list].sort((a, b) => priceNum(b) - priceNum(a));
    return list;
  }, [products, category, query, status, sort]);

  return (
    <div className="container mx-auto px-4 py-4">
      <div className="mb-4 overflow-hidden rounded-lg border border-border/60 bg-gradient-to-br from-primary/15 via-card to-card p-4 sm:p-5">
        <div className="flex items-center gap-3">
          <Package className="h-6 w-6 text-primary shrink-0" />
          <div className="min-w-0 flex-1">
            <EditableText textKey="collection.order.banner.title" defaultValue="Hàng mới về"
              className="block text-sm sm:text-base font-bold" />
            <EditableText textKey="collection.order.banner.sub"
              defaultValue="Wig, phụ kiện, đồ 2hand mình đã order về. Thấy món nào ưng — nhắn mình giữ chỗ."
              className="block text-xs text-muted-foreground" />
          </div>
        </div>
      </div>

      <div className="mb-3 flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-[180px] max-w-md">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <input value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder="Tìm món..."
            className="h-9 w-full rounded-md border border-border/60 bg-background pl-8 pr-3 text-sm" />
        </div>
        <select value={sort} onChange={(e) => setSort(e.target.value as any)}
          className="h-9 rounded-md border border-border/60 bg-background px-2 text-xs">
          <option value="new">Mới nhất</option>
          <option value="price-asc">Giá thấp → cao</option>
          <option value="price-desc">Giá cao → thấp</option>
        </select>
      </div>

      <div className="mb-3 flex flex-wrap gap-1.5">
        <Chip active={status === "all"} onClick={() => setStatus("all")}>Tất cả</Chip>
        <Chip active={status === "available"} onClick={() => setStatus("available")}>Còn hàng</Chip>
        <Chip active={status === "sold"} onClick={() => setStatus("sold")}>Đã bán</Chip>
      </div>

      <div className="-mx-4 mb-4 overflow-x-auto px-4">
        <div className="flex gap-1.5 whitespace-nowrap">
          <Chip active={category === "all"} onClick={() => setCategory("all")}>Tất cả ({products.length})</Chip>
          {categories.map(([c, n]) => (
            <Chip key={c} active={category === c} onClick={() => setCategory(c)}>{c} ({n})</Chip>
          ))}
        </div>
      </div>

      <div className="mb-2 text-xs text-muted-foreground">{filtered.length} món</div>

      {loading ? (
        <SkeletonGrid cols="grid-cols-2 sm:grid-cols-3 lg:grid-cols-4" />
      ) : filtered.length === 0 ? (
        <EmptyState msg="Chưa có món nào." />
      ) : (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 sm:gap-3 lg:grid-cols-4">
          {filtered.map((p) => {
            const cover = p.cover_image ?? p.images[0];
            const sold = isSold(p);
            const fresh = isFresh(p);
            return (
              <button key={p.id} onClick={() => onOpen(p, filtered)}
                className="group flex flex-col overflow-hidden rounded-md border border-border/60 bg-card text-left transition hover:shadow-[var(--glow-pink)]">
                <div className="relative aspect-square overflow-hidden bg-muted">
                  {cover ? (
                    <img src={cover} alt={p.name} loading="lazy"
                      className={`h-full w-full object-cover transition-transform duration-500 group-hover:scale-105 ${sold ? "grayscale" : ""}`} />
                  ) : (
                    <div className="grid h-full place-items-center text-muted-foreground"><Sparkles className="h-6 w-6 opacity-40" /></div>
                  )}
                  {fresh && !sold && (
                    <span className="absolute left-1.5 top-1.5 rounded bg-primary px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-primary-foreground shadow">
                      Mới về
                    </span>
                  )}
                  {sold && (
                    <div className="absolute inset-0 grid place-items-center bg-black/35">
                      <span className="rotate-[-12deg] rounded border-2 border-white px-3 py-1 text-sm font-extrabold uppercase tracking-widest text-white">Sold</span>
                    </div>
                  )}
                </div>
                <div className="p-2">
                  <div className="line-clamp-1 text-xs sm:text-sm font-medium">{p.name}</div>
                  <div className="mt-1 flex items-end justify-between gap-2">
                    <div className="text-sm sm:text-base font-bold text-primary">
                      {p.price || <span className="text-muted-foreground font-normal">Hỏi giá</span>}
                    </div>
                    {!sold && (
                      <a href={`${MESSENGER_URL}?ref=${encodeURIComponent(p.slug)}`} target="_blank" rel="noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="inline-flex shrink-0 items-center gap-1 rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-semibold text-primary hover:bg-primary hover:text-primary-foreground">
                        <MessageCircle className="h-3 w-3" /> Hỏi
                      </a>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ============================================================ Product viewer (Instagram-style) */

function ProductViewer({
  variant, selected, images, imageIdx, setImageIdx, filtered, setSelected,
  detailsOpen, setDetailsOpen, showHint, setShowHint, onClose,
}: {
  variant: ViewerVariant;
  selected: Product; images: string[]; imageIdx: number;
  setImageIdx: (fn: (i: number) => number) => void;
  filtered: Product[]; setSelected: (p: Product | null) => void;
  detailsOpen: boolean; setDetailsOpen: (b: boolean) => void;
  showHint: boolean; setShowHint: (b: boolean) => void;
  onClose: () => void;
}) {
  const touchRef = useRef<{ x: number; y: number; t: number } | null>(null);
  const productIdx = filtered.findIndex((p) => p.id === selected.id);

  function gotoImage(dir: -1 | 1) {
    if (images.length < 2) return;
    setImageIdx((i) => (i + dir + images.length) % images.length);
  }
  function gotoProduct(dir: -1 | 1) {
    if (filtered.length < 2 || productIdx < 0) return;
    const next = (productIdx + dir + filtered.length) % filtered.length;
    setSelected(filtered[next]);
    setImageIdx(() => 0);
    setDetailsOpen(false);
  }

  useEffect(() => {
    setShowHint(true);
    const id = window.setTimeout(() => setShowHint(false), 3000);
    return () => window.clearTimeout(id);
  }, [selected.id, setShowHint]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") { onClose(); return; }
      if (e.key === "ArrowLeft")  gotoImage(-1);
      if (e.key === "ArrowRight") gotoImage(1);
      if (e.key === "ArrowUp")    gotoProduct(1);
      if (e.key === "ArrowDown")  gotoProduct(-1);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected.id, images.length, productIdx, filtered.length]);

  function onTouchStart(e: React.TouchEvent) {
    const t = e.touches[0];
    touchRef.current = { x: t.clientX, y: t.clientY, t: Date.now() };
  }
  function onTouchEnd(e: React.TouchEvent) {
    const s = touchRef.current; touchRef.current = null;
    if (!s) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - s.x, dy = t.clientY - s.y;
    const ax = Math.abs(dx), ay = Math.abs(dy);
    const THRESH = 50;
    if (Math.max(ax, ay) < THRESH) return;
    if (ax > ay) gotoImage(dx < 0 ? 1 : -1);
    else if (dy < 0) gotoProduct(1);
    else onClose();
  }

  function onImageClick(e: React.MouseEvent<HTMLDivElement>) {
    if (images.length < 2) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    gotoImage(x < rect.width / 2 ? -1 : 1);
  }

  const cap = images.length ? selected.image_meta?.[images[imageIdx]]?.caption : undefined;
  const sub = [selected.fandom, selected.character_name].filter(Boolean).join(" · ");

  const isSample = variant === "sample";
  const showPrice = (variant === "order" || variant === "craft") && !!selected.price;
  const ctaLabel = variant === "order" ? "Hỏi mua" : "Nhắn";
  const nameFont = variant === "craft" || variant === "sample" ? "font-serif" : "";

  return (
    <div className="fixed inset-0 z-50 bg-black/95" role="dialog" aria-modal="true">
      <div
        className="absolute inset-0 select-none"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        onClick={onImageClick}
      >
        {images.length > 0 ? (
          <img
            key={images[imageIdx]}
            src={images[imageIdx]}
            alt={selected.name}
            className="h-full w-full object-contain animate-in fade-in duration-200"
            draggable={false}
          />
        ) : (
          <div className="grid h-full w-full place-items-center text-white/60">
            <Sparkles className="h-10 w-10 opacity-40" />
          </div>
        )}
      </div>

      <button
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        aria-label="Đóng"
        className="absolute right-3 top-3 z-20 grid h-10 w-10 place-items-center rounded-full bg-white/15 text-white backdrop-blur transition hover:bg-white/25"
      >
        <X className="h-5 w-5" />
      </button>

      {images.length > 1 && (
        <>
          <button
            onClick={(e) => { e.stopPropagation(); gotoImage(-1); }}
            aria-label="Ảnh trước"
            className="absolute left-4 top-1/2 z-20 hidden h-12 w-12 -translate-y-1/2 place-items-center rounded-full bg-white/15 text-white backdrop-blur transition hover:scale-105 hover:bg-white/25 sm:grid"
          >
            <CornerUpLeft className="h-6 w-6 -rotate-90" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); gotoImage(1); }}
            aria-label="Ảnh sau"
            className="absolute right-4 top-1/2 z-20 hidden h-12 w-12 -translate-y-1/2 place-items-center rounded-full bg-white/15 text-white backdrop-blur transition hover:scale-105 hover:bg-white/25 sm:grid"
          >
            <CornerUpRight className="h-6 w-6 rotate-90" />
          </button>
        </>
      )}

      {images.length > 1 && (
        <div className="absolute left-1/2 top-4 z-20 flex -translate-x-1/2 gap-1.5">
          {images.map((_, i) => (
            <span
              key={i}
              className={`h-1.5 rounded-full transition-all ${i === imageIdx ? "w-5 bg-white" : "w-1.5 bg-white/40"}`}
            />
          ))}
        </div>
      )}

      <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 bg-gradient-to-t from-black/90 via-black/40 to-transparent p-4 pt-12 sm:p-6 sm:pt-16">
        <div className="pointer-events-auto mx-auto flex max-w-3xl items-end justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className={`truncate text-sm font-medium text-white sm:text-base ${nameFont}`}>{selected.name}</div>
            {sub && <div className="truncate text-[11px] text-white/70 sm:text-xs">{sub}</div>}
            {showPrice && (
              <div className="mt-1 text-sm font-bold text-primary sm:text-base">{selected.price}</div>
            )}
            {cap && <div className="mt-1 line-clamp-2 text-[11px] text-white/60 sm:text-xs">{cap}</div>}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <button
              onClick={(e) => { e.stopPropagation(); setDetailsOpen(!detailsOpen); }}
              className="rounded-full bg-white/15 px-3 py-1.5 text-xs font-medium text-white backdrop-blur transition hover:bg-white/25"
            >
              Chi tiết
            </button>
            {!isSample && (
              <a
                href={`${MESSENGER_URL}?ref=${encodeURIComponent(selected.slug)}`}
                target="_blank"
                rel="noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-1.5 rounded-full bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground shadow-[var(--glow-pink)]"
              >
                <MessageCircle className="h-3.5 w-3.5" /> {ctaLabel}
              </a>
            )}
          </div>
        </div>
      </div>

      {showHint && filtered.length > 1 && (
        <div className="pointer-events-none absolute bottom-24 left-1/2 z-20 flex -translate-x-1/2 flex-col items-center gap-1 text-white/80 animate-in fade-in duration-500 sm:bottom-28">
          <ChevronsUp className="h-6 w-6 animate-bounce" />
          <span className="rounded-full bg-black/50 px-2.5 py-0.5 text-[10px] backdrop-blur">
            Vuốt lên để xem món tiếp theo
          </span>
        </div>
      )}

      {detailsOpen && (
        <div
          className="absolute inset-x-0 bottom-0 z-30 max-h-[75vh] overflow-y-auto rounded-t-2xl border-t border-border bg-background p-5 shadow-2xl animate-in slide-in-from-bottom duration-300"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="mx-auto mb-3 h-1 w-12 rounded-full bg-muted" />
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className={`text-lg font-semibold leading-tight ${nameFont}`}>{selected.name}</h2>
              {selected.character_name && (
                <p className="text-xs text-muted-foreground">Nhân vật: {selected.character_name}</p>
              )}
            </div>
            <button
              onClick={() => setDetailsOpen(false)}
              className="rounded-md p-1 hover:bg-muted"
              aria-label="Đóng chi tiết"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          {selected.description && (
            <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-foreground/90">{selected.description}</p>
          )}
          <div className="mt-4 space-y-2 text-sm">
            {selected.price && <Row label="Giá" value={selected.price} valueClass="text-primary font-semibold" />}
            {selected.materials && <Row label="Chất liệu" value={selected.materials} />}
            {Object.entries(selected.attributes ?? {})
              .filter(([k]) => !["category", "fandom", "material", "price_tier", "mode"].includes(k))
              .map(([k, v]) => (
                <Row key={k} label={k} value={String(v)} />
              ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, value, valueClass }: { label: string; value: string; valueClass?: string }) {
  return (
    <div className="flex gap-3 border-b border-border/40 pb-2">
      <span className="w-32 shrink-0 text-muted-foreground">{label}</span>
      <span className={`flex-1 ${valueClass ?? ""}`}>{value}</span>
    </div>
  );
}
